import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnyoSortDirection } from 'projects/app-core/src/models/gridRequest';
import { LyfisService } from '../../../services/lyfis.service';
import {
  LyfisPersistedUserScoresFilter,
  LyfisPersistedUserScoresGridRequest,
  LyfisPersistedUserScoresSearch,
  LyfisPersistedUserScoresSort,
  LyfisPersistedUserSnapshotRow,
} from '../models/lyfis-persisted-user-scores-grid.models';

interface CheckboxOptions {
  label: string;
  value: string;
  checked: boolean;
}

@Component({
  selector: 'app-lyfis-view-user-snapshots',
  templateUrl: './lyfis-view-user-snapshots.component.html',
})
export class LyfisViewUserSnapshotsComponent {
  loading = false;
  rows: LyfisPersistedUserSnapshotRow[] = [];
  userId = '';
  page = 1;
  pageSize = 20;
  total = 0;

  partnerSearch = '';
  showTesting = false;

  triggerCheckBoxOptions: CheckboxOptions[] = [];
  ghsBandCheckBoxOptions: CheckboxOptions[] = [];
  statusCheckBoxOptions: CheckboxOptions[] = [];
  partnerIdCheckBoxOptions: CheckboxOptions[] = [];

  selectedTriggers: string[] = [];
  selectedBands: string[] = [];
  selectedStatuses: string[] = [];
  selectedPartnerIds: string[] = [];

  sortKey: LyfisPersistedUserScoresSort = LyfisPersistedUserScoresSort.CREATED_AT;
  sortDirection = AnyoSortDirection.DESC;

  sortOptions = [
    { label: 'Created At', value: LyfisPersistedUserScoresSort.CREATED_AT },
    { label: 'GHS', value: LyfisPersistedUserScoresSort.GHS },
    {
      label: 'Disease Score Count',
      value: LyfisPersistedUserScoresSort.DISEASE_SCORE_IDS_COUNT,
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private lyfisService: LyfisService,
  ) {
    this.userId = this.route.snapshot.paramMap.get('userId') || '';
    void this.fetch();
  }

  fetch(): Promise<void> {
    this.loading = true;
    const req: LyfisPersistedUserScoresGridRequest = {
      page: this.page,
      pageSize: this.pageSize,
      export: false,
      searchQuery: {
        [LyfisPersistedUserScoresSearch.PARTNER_ID]: this.partnerSearch || undefined,
      },
      filters: {
        [LyfisPersistedUserScoresFilter.TRIGGER]: this.selectedTriggers.length
          ? this.selectedTriggers
          : undefined,
        [LyfisPersistedUserScoresFilter.GHS_BAND]: this.selectedBands.length
          ? this.selectedBands
          : undefined,
        [LyfisPersistedUserScoresFilter.GLOBAL_INSIGHTS_GENERATING_STATUS]:
          this.selectedStatuses.length ? this.selectedStatuses : undefined,
        [LyfisPersistedUserScoresFilter.PARTNER_ID]: this.selectedPartnerIds.length
          ? this.selectedPartnerIds
          : undefined,
        [LyfisPersistedUserScoresFilter.TEST_USERS]: [String(this.showTesting)],
      },
      sort: {
        [LyfisPersistedUserScoresSort.CREATED_AT]:
          this.sortKey === LyfisPersistedUserScoresSort.CREATED_AT
            ? this.sortDirection
            : undefined,
        [LyfisPersistedUserScoresSort.GHS]:
          this.sortKey === LyfisPersistedUserScoresSort.GHS
            ? this.sortDirection
            : undefined,
        [LyfisPersistedUserScoresSort.DISEASE_SCORE_IDS_COUNT]:
          this.sortKey === LyfisPersistedUserScoresSort.DISEASE_SCORE_IDS_COUNT
            ? this.sortDirection
            : undefined,
      },
    };
    return this.lyfisService
      .getLyfisPersistedUserSnapshotsGrid(this.userId, req)
      .toPromise()
      .then((res) => {
        this.rows = res?.data || [];
        this.total = res?.totalRecords || 0;
        
        this.setTriggerFilterData(res?.filterOptions?.trigger);
        this.setGhsBandFilterData(res?.filterOptions?.ghsBand);
        this.setStatusFilterData(res?.filterOptions?.globalInsightsGeneratingStatus);
        this.setPartnerIdFilterData(res?.filterOptions?.partnerId);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  setTriggerFilterData(options?: string[]) {
    this.triggerCheckBoxOptions = [];
    options?.map((data) => {
      this.triggerCheckBoxOptions.push({
        checked: false,
        label: data,
        value: data,
      });
    });
  }

  setGhsBandFilterData(options?: string[]) {
    this.ghsBandCheckBoxOptions = [];
    options?.map((data) => {
      this.ghsBandCheckBoxOptions.push({
        checked: false,
        label: data,
        value: data,
      });
    });
  }

  setStatusFilterData(options?: string[]) {
    this.statusCheckBoxOptions = [];
    options?.map((data) => {
      this.statusCheckBoxOptions.push({
        checked: false,
        label: data,
        value: data,
      });
    });
  }

  setPartnerIdFilterData(options?: string[]) {
    this.partnerIdCheckBoxOptions = [];
    options?.map((data) => {
      this.partnerIdCheckBoxOptions.push({
        checked: false,
        label: data,
        value: data,
      });
    });
  }

  triggerFilterFn() {
    this.page = 1;
    void this.fetch();
  }

  triggerFilterChangeFn($event: string[]) {
    this.selectedTriggers = $event;
  }

  ghsBandFilterFn() {
    this.page = 1;
    void this.fetch();
  }

  ghsBandFilterChangeFn($event: string[]) {
    this.selectedBands = $event;
  }

  statusFilterFn() {
    this.page = 1;
    void this.fetch();
  }

  statusFilterChangeFn($event: string[]) {
    this.selectedStatuses = $event;
  }

  partnerIdFilterFn() {
    this.page = 1;
    void this.fetch();
  }

  partnerIdFilterChangeFn($event: string[]) {
    this.selectedPartnerIds = $event;
  }

  testFilterFn() {
    this.page = 1;
    void this.fetch();
  }

  clearFilter() {
    this.partnerSearch = '';
    this.selectedTriggers = [];
    this.selectedBands = [];
    this.selectedStatuses = [];
    this.selectedPartnerIds = [];
    this.showTesting = false;
    
    this.triggerCheckBoxOptions = [];
    this.ghsBandCheckBoxOptions = [];
    this.statusCheckBoxOptions = [];
    this.partnerIdCheckBoxOptions = [];
    
    this.sortKey = LyfisPersistedUserScoresSort.CREATED_AT;
    this.sortDirection = AnyoSortDirection.DESC;
    this.page = 1;
    void this.fetch();
  }

  openSnapshot(row: LyfisPersistedUserSnapshotRow) {
    this.router.navigate(['/lyfis/view-scores/snapshots', row.id, 'diseases']);
  }

  onPageIndexChange(page: number) {
    this.page = page;
    void this.fetch();
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize = pageSize;
    this.page = 1;
    void this.fetch();
  }

  applyFilters() {
    this.page = 1;
    void this.fetch();
  }
}
